import ConversationApi from 'dashboard/api/inbox/conversation';
import types from '../../../mutation-types';
import { mergeRealtimeMessages } from '../helpers/realtimeHelpers';
import { emitter } from 'shared/helpers/mitt';

// Promises belong to the store instance, rather than serializable Vuex state.
const hydrations = new WeakMap();
const RETRY_DELAYS = [1000, 2000];

const hydrate = async (context, conversationId, accountId) => {
  const { state, commit, dispatch, rootGetters } = context;
  for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt += 1) {
    if (rootGetters.getCurrentAccountId !== accountId) return;
    if (state.allConversations.some(chat => chat.id === conversationId)) {
      dispatch('flushPendingRealtimeMessages', conversationId);
      return;
    }
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await ConversationApi.show(conversationId);
      if (rootGetters.getCurrentAccountId !== accountId) return;
      commit(types.UPSERT_REALTIME_CONVERSATION, data);
      const conversation = state.allConversations.find(
        chat => chat.id === conversationId
      );
      dispatch('conversationLabels/setConversationLabel', {
        id: conversationId,
        data: conversation.labels,
      });
      if (conversation.meta?.sender) {
        dispatch('contacts/setContact', conversation.meta.sender);
      }
      dispatch('flushPendingRealtimeMessages', conversationId);
      emitter.emit('fetch_conversation_stats');
      return;
    } catch (error) {
      if (rootGetters.getCurrentAccountId !== accountId) return;
      if (state.allConversations.some(chat => chat.id === conversationId)) {
        dispatch('flushPendingRealtimeMessages', conversationId);
        return;
      }
      // Never log the response, message content, auth headers or contact data.
      // eslint-disable-next-line no-console
      console.warn('Realtime conversation hydration failed', {
        conversationId,
        attempt: attempt + 1,
        status: error.response?.status,
      });
      if (attempt === RETRY_DELAYS.length) {
        commit(types.SET_REALTIME_HYDRATION_FAILED, conversationId);
        return;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => {
        setTimeout(resolve, RETRY_DELAYS[attempt]);
      });
    }
  }
};

export default {
  realtimeAddMessage(context, message) {
    const { state, commit, dispatch, rootGetters } = context;
    const accountId = rootGetters.getCurrentAccountId;
    if (state.realtimeAccountId !== accountId) {
      commit(types.RESET_REALTIME_MESSAGES, accountId);
    }
    const conversationId = message.conversation_id;
    if (state.allConversations.some(chat => chat.id === conversationId)) {
      dispatch('flushPendingRealtimeMessages', conversationId);
      return dispatch('addMessage', message);
    }
    commit(types.BUFFER_REALTIME_MESSAGE, message);
    if (state.realtimeHydrationFailures[conversationId]) return undefined;
    let requests = hydrations.get(state);
    if (!requests) {
      requests = new Map();
      hydrations.set(state, requests);
    }
    const key = `${accountId}:${conversationId}`;
    if (!requests.has(key)) {
      const request = Promise.resolve()
        .then(() => hydrate(context, conversationId, accountId))
        .finally(() => requests.delete(key));
      requests.set(key, request);
    }
    return requests.get(key);
  },

  flushPendingRealtimeMessages({ state, commit, dispatch, rootGetters }, id) {
    if (state.realtimeAccountId !== rootGetters?.getCurrentAccountId) return;
    const ids = id ? [id] : Object.keys(state.pendingRealtimeMessages || {});
    ids.forEach(conversationId => {
      const chat = state.allConversations.find(
        conversation => conversation.id === Number(conversationId)
      );
      const pending = state.pendingRealtimeMessages[conversationId];
      if (!chat || !pending?.length) return;
      const ordered = [...pending].sort(
        (a, b) => a.created_at - b.created_at || a.id - b.id
      );
      const latest = ordered[ordered.length - 1];
      const unreadCount =
        latest.conversation?.last_activity_at > (chat.last_activity_at || 0)
          ? latest.conversation.unread_count
          : chat.unread_count;
      ordered.forEach(message => {
        const current = mergeRealtimeMessages(chat.messages, [message]).find(
          item =>
            item.id === message.id ||
            item.echo_id === message.id ||
            (message.echo_id &&
              (item.id === message.echo_id || item.echo_id === message.echo_id))
        );
        dispatch('addMessage', {
          ...current,
          conversation: { ...current.conversation, unread_count: unreadCount },
        });
        commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
          conversationId: chat.id,
          lastActivityAt: message.conversation?.last_activity_at,
        });
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: chat.id,
        data: mergeRealtimeMessages(chat.messages),
      });
      commit(types.CLEAR_REALTIME_MESSAGES, chat.id);
    });
  },
};
